import { ItemField } from 'src/interfaces/item'
import { LayoutOptions } from '../../../interfaces/layout'
import { deepMerge } from '../../../utils/deepMerge'
import { ItemCardProps } from './ItemCard.types'

export const defaultProps: Partial<ItemCardProps> = {
  price: {
    size: 16,
    discountSize: 12,
    unitsSize: 12,
    showIcon: false,
    gap: 3
  },
  tags: {
    items: ['item.linkedProducts', 'item.attributes', 'item.stock'],
    map: {
      stock: {
        props: {
          icon: 'MdAllInbox',
          label: '{{value}} disponibles'
        }
      },
      returnable: {
        show: false,
        props: {
          icon: 'MdAutorenew',
          label: 'Returnable'
        }
      },
      alcohol: {
        show: false,
        props: {
          icon: 'MdLocalBar',
          label: 'Alcohol'
        }
      },
      linkedProducts: {
        noRepeat: true,
        props: {
          label: 'Associated product',
          variant: 'outlined'
        }
      }
    }
  },
  sideContent: {
    items: ['item.attributes'],
    map: {
      returnable: {
        type: 'tag',
        area: 'right',
        side: 'start',
        float: true,
        props: {
          icon: 'MdAutorenew',
          label: 'Retur'
        }
      },
      alcohol: {
        type: 'tag',
        area: 'right',
        side: 'start',
        float: true,
        props: {
          icon: 'MdLocalBar',
          label: 'Alc'
        }
      }
    }
  },
  packagePriceMap: {
    'half box': 'packagePrice',
    unit: 'unitPrice'
  }
}

export const defaultLayout = deepMerge(defaultProps, {
  padding: 8,
  header: {
    padding: 0,
    align: 'center',
    alignContent: 'center'
  },
  body: {
    padding: '0 4px',
    fields: [
      ItemField.Name,
      ItemField.PromotionTag,
      ItemField.PromotionSavingLabel,
      ItemField.Description,
      ItemField.Tags
    ],
    gap: 4,
    align: 'center'
  },
  footer: {
    padding: 0,
    fields: [ItemField.Price, ItemField.Counter],
    align: 'center'
  },
  image: {
    ratio: 65,
    radius: 4
  },
  name: {
    size: 12,
    variant: 'h2',
    lines: 2
  },
  description: {
    size: 10,
    lines: 2
  },
  counter: {
    styles: {
      maxWidth: 100
    }
  },
  promotionTag: {
    wrap: true,
    size: 10
  }
})

export const horizontalLayout = deepMerge(defaultLayout, {
  horizontal: true,
  header: {
    padding: '0 0 0 12px'
  },
  body: {
    align: 'left',
    fields: [
      ItemField.Name,
      ItemField.PromotionTag,
      ItemField.PromotionSavingLabel,
      ItemField.Description
    ]
  },
  footer: {
    fields: [ItemField.Price, ItemField.Counter, ItemField.Tags],
    align: 'left',
    columns: 2,
    alignItems: 'center',
    padding: '0 0 2px 0'
  },
  image: {
    size: 75,
    ratio: 134
  },
  name: {
    lines: 1,
    align: 'left'
  },
  description: {
    lines: 1,
    align: 'left'
  },
  price: {
    showIcon: false
  },
  promotionTag: {
    wrap: false
  },
  tags: {
    align: 'left',
    noWrap: true,
    styles: {
      gridColumn: '1 / 3'
    }
  },
  counter: {
    styles: {
      justifySelf: 'end'
    }
  },
  sideContent: {
    map: {
      returnable: {
        area: 'left',
        side: 'end'
      },
      alcohol: {
        area: 'left',
        side: 'end'
      }
    }
  }
})

export const classicLayout = deepMerge(defaultProps, {
  padding: 8,
  header: {
    padding: 0,
    alignContent: 'center'
  },
  body: {
    padding: '0 4px',
    fields: [
      ItemField.Name,
      ItemField.Description,
      ItemField.Price,
      ItemField.Tags
    ],
    gap: 4
  },
  footer: {
    padding: 0,
    fields: [ItemField.Counter]
  },
  image: {
    ratio: 65,
    radius: 4
  },
  name: {
    size: 13,
    variant: 'h2',
    lines: 2
  },
  description: {
    size: 12,
    lines: 2
  },
  price: {
    size: 13
  },
  tags: {
    noWrap: true,
    map: {
      stock: {
        priority: 0
      },
      returnable: {
        show: true
      },
      alcohol: {
        show: true
      }
    }
  },
  sideContent: {
    items: []
  }
})

export const classicHorizontalLayout = deepMerge(classicLayout, {
  horizontal: true,
  image: {
    size: 104,
    ratio: 100
  },
  name: {
    lines: 0
  },
  description: {
    lines: 1
  },
  counter: {
    styles: {
      maxWidth: 140
    }
  },
  price: {
    showIcon: false
  }
})

const condensedLayout = deepMerge(horizontalLayout, {
  footer: {
    fields: [ItemField.Price],
    columns: 1
  },
  image: {
    size: 88,
    ratio: 70
  },
  name: {
    lines: 1
  }
})

const cartLayout = deepMerge(horizontalLayout, {
  padding: '8px 0 8px 8px',
  image: {
    size: 48,
    ratio: 100
  },
  body: {
    align: 'left',
    fields: [
      ItemField.Name,
      ItemField.PromotionTag,
      ItemField.PromotionSavingLabel,
      ItemField.Description
    ]
  },
  header: {
    padding: 0
  },
  footer: {
    fields: [ItemField.Price, ItemField.Counter]
  },
  fixedContentBody: {
    fields: [ItemField.Name, ItemField.PromotionTag, ItemField.PromotionOrigin]
  },
  sideContent: {
    items: ['item.attributes', 'deleteButton'],
    map: {
      deleteButton: {
        type: 'iconButton',
        area: 'right',
        side: 'end',
        bottom: 8,
        disableWhenReadOnly: true,
        props: {
          icon: 'MdDeleteOutline'
        }
      },
      returnable: {
        area: 'right',
        side: 'start',
        float: false
      },
      alcohol: {
        area: 'right',
        side: 'start',
        float: false
      }
    }
  },
  showFixedBottomContent: true
})

const messageLayout = deepMerge(cartLayout, {
  elevation: 0,
  header: {
    padding: 0
  },
  footer: {
    fields: []
  },
  sideContent: {
    items: []
  }
})

const sideCounterLayout = deepMerge(classicLayout, {
  body: {
    fields: [ItemField.Name, ItemField.Description, ItemField.Tags]
  },
  footer: {
    fields: [ItemField.Price, ItemField.Counter],
    padding: '0 4px 0',
    alignContent: 'center',
    styles: {
      minHeight: '24px'
    }
  },
  counter: {
    sideCounter: true,
    styles: {
      justifySelf: 'end'
    }
  }
})

const detailLayout = deepMerge(defaultProps, {
  body: {
    fields: [
      ItemField.Name,
      ItemField.Description,
      ItemField.Price,
      ItemField.Tags
    ]
  },
  footer: {
    fields: [
      ItemField.PromotionDetail,
      ItemField.PromotionsDetails,
      ItemField.ProductOptions,
      ItemField.ProductPackages
    ]
  },
  tags: {
    noWrap: false,
    map: {
      stock: {
        priority: 0
      },
      returnable: {
        show: true
      },
      alcohol: {
        show: true
      }
    }
  },
  sideContent: {
    items: []
  }
})

export const catalogCardLayout = deepMerge(defaultProps, {
  padding: 8,
  header: {
    padding: 0,
    align: 'center',
    alignContent: 'center'
  },
  body: {
    padding: '0 4px',
    fields: [
      ItemField.Name,
      ItemField.SimplePromotionTag,
      ItemField.LimitText,
      ItemField.Stock,
      ItemField.Description,
      ItemField.Tags
    ],
    gap: 4,
    align: 'center'
  },
  footer: {
    padding: 0,
    fields: [ItemField.Price, ItemField.Counter],
    align: 'center'
  },
  image: {
    ratio: 65,
    radius: 4
  },
  name: {
    size: 10,
    variant: 'h2',
    lines: 2,
    styles: {
      fontStyle: 'normal',
      fontWeight: 700,
      lineHeight: '130%'
    }
  },
  description: {
    size: 9,
    lines: 1,
    styles: {
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '12px'
    }
  },
  price: {
    size: 15,
    discountSize: 11,
    unitsSize: 11,
    showIcon: false,
    gap: 3,
    styles: {
      alignItems: 'baseline'
    }
  },
  promotionTag: {
    wrap: false,
    size: 10
  },
  sideContent: {
    items: ['item.attributes'],
    map: {
      returnable: {
        type: 'tag',
        area: 'right',
        side: 'start',
        float: true,
        props: {
          icon: {
            name: 'CmProductBox',
            iconset: 'cm'
          },
          label: {
            value: 'Caja',
            styles: {
              fontWeight: 500
            }
          }
        }
      },
      box: {
        type: 'tag',
        area: 'right',
        side: 'start',
        float: true,
        props: {
          icon: {
            name: 'CmReturnable',
            iconset: 'cm'
          },
          label: {
            value: 'Retor',
            styles: {
              fontWeight: 500
            }
          }
        }
      },
      alcohol: {
        type: 'tag',
        area: 'right',
        side: 'start',
        float: true,
        props: {
          icon: {
            name: 'CmAlcohol',
            iconset: 'cm'
          },
          label: {
            value: 'Alc',
            styles: {
              fontWeight: 500
            }
          }
        }
      }
    }
  }
})

export const horizontalCatalogCardLayout = deepMerge(defaultProps, {
  horizontal: true,
  padding: 8,
  header: {
    padding: 0,
    align: 'left',
    alignContent: 'center'
  },
  body: {
    padding: '0 4px',
    fields: [
      ItemField.Name,
      ItemField.SimplePromotionTag,
      ItemField.LimitText,
      ItemField.Tags,
      ItemField.Price
    ],
    gap: 4,
    align: 'left'
  },
  footer: {
    fields: [ItemField.DescriptionStock, ItemField.Counter],
    align: 'left',
    columns: 2,
    alignItems: 'center',
    padding: '0 0 2px 0'
  },
  counter: {
    styles: {
      justifySelf: 'end'
    }
  },
  image: {
    size: 96,
    ratio: 100
  },
  name: {
    size: 10,
    variant: 'h2',
    lines: 1,
    styles: {
      fontStyle: 'normal',
      fontWeight: 700,
      lineHeight: '130%'
    }
  },
  description: {
    size: 9,
    lines: 1,
    styles: {
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '11px'
    }
  },
  price: {
    size: 15,
    discountSize: 11,
    unitsSize: 11,
    showIcon: false,
    gap: 3,
    styles: {
      alignItems: 'baseline'
    }
  },
  promotionTag: {
    wrap: false,
    size: 10
  },
  sideContent: {
    items: ['item.attributes'],
    map: {
      returnable: {
        type: 'tag',
        area: 'left',
        side: 'start',
        float: true,
        props: {
          icon: {
            name: 'CmProductBox',
            iconset: 'cm'
          },
          label: {
            value: 'Caja',
            styles: {
              fontWeight: 500
            }
          }
        }
      },
      box: {
        type: 'tag',
        area: 'left',
        side: 'start',
        float: true,
        props: {
          icon: {
            name: 'CmReturnable',
            iconset: 'cm'
          },
          label: {
            value: 'Retor',
            styles: {
              fontWeight: 500
            }
          }
        }
      },
      alcohol: {
        type: 'tag',
        area: 'left',
        side: 'start',
        float: true,
        props: {
          icon: {
            name: 'CmAlcohol',
            iconset: 'cm'
          },
          label: {
            value: 'Alc',
            styles: {
              fontWeight: 500
            }
          }
        }
      }
    }
  }
})

const detailFullPageLayout = deepMerge(detailLayout, {
  body: {
    fields: [
      ItemField.Name,
      ItemField.Description,
      ItemField.Price,
      ItemField.Tags,
    ]
  },
  footer: {
    fields: [
      ItemField.PromotionDetail,
      ItemField.VolumePromotions,
      ItemField.PromotionsDetails,
      ItemField.ProductOptions,
      ItemField.ProductPackages
    ]
  },
  promotionsTable: {}
})

const cartCondensedLayout = deepMerge(cartLayout, {
  body: {
    align: 'left',
    fields: [
      ItemField.Name,
      ItemField.PromotionTag,
      ItemField.Price,
      ItemField.PromotionSavingLabel,
      ItemField.Description
    ]
  },
  footer: {
    fields: [ItemField.PriceTotal, ItemField.Counter]
  },
  price: {
    size: 12
  }
})

export const itemCardLayouts: Record<LayoutOptions, Partial<ItemCardProps>> = {
  none: defaultProps,
  default: defaultLayout,
  horizontal: horizontalLayout,
  condensed: condensedLayout,
  classic: classicLayout,
  classicHorizontal: classicHorizontalLayout,
  cart: cartLayout,
  cartCondensed: cartCondensedLayout,
  message: messageLayout,
  recommended: sideCounterLayout,
  detail: detailLayout,
  catalogCard: catalogCardLayout,
  horizontalCatalogCard: horizontalCatalogCardLayout,
  detailFullPage: detailFullPageLayout
}
